import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { onAuthStateChanged, type Auth, type User } from 'firebase/auth';
import { useAuth } from '../../../theaWeb/firebase/FirebaseContext';
import { useIsSignedIn } from '../../../theaWeb/hooks/useIsSignedIn';
import { AuthGateContext } from '../../../theaWeb/auth/AuthGateContext';
import { HomeSkeleton } from './HomeSkeleton';
import { useFriendsList } from '../dashboard/useFriendsList';
import { useDesktopNav } from '../../../theaWeb/hooks/useDesktopNav';
import {
  readHasBoardsHint,
  writeHasBoardsHint,
} from '../../../theaWeb/lib/hasBoardsHint';
import { SiteHeader } from '../SiteHeader';
import { HeaderAccountMenu } from '../../../theaWeb/auth/HeaderAccountMenu';
// v4 preview (?v4=1): the flag-ON homepage door. Flag-OFF is byte-identical to
// today's production — the branches below fall through to the existing nodes.
import { isV4Preview } from '../../../theaWeb/lib/v4Preview';
import { V4HomeDoor } from '../../../theaWeb/v4/V4HomeDoor';
import { V4SignInModal } from '../../../theaWeb/v4/V4SignInModal';
import type { BrowserKind } from '../../../theaWeb/v4/accountState';
import { isInAppBrowser } from '../../../theaWeb/lib/inAppBrowser';
import { HomepageHero } from './HomepageHero';
import { FeatureStack } from './FeatureStack';
import { EmotionalBanner } from './EmotionalBanner';
import {
  HOMEPAGE_STACK_FRAMES,
  HOMEPAGE_BANNER_HEADLINE,
  HOMEPAGE_HERO_SUBHEAD,
  HOMEPAGE_TESTIMONIALS,
} from './homepageStackFrames';
import { HomeGuideRail } from './HomeGuideRail';
import { TestimonialsCarousel, type Testimonial } from './TestimonialsCarousel';
import { BrowseMyFriendsSection } from './BrowseMyFriendsSection';
import type {
  DashboardPerson,
  FriendPreviewLoader,
} from '../dashboard/types';
// Gift guides hub (?giftGuideHub=1 / the release switch): the People tab's
// share of THE-816. `useHubFrame` reads the ALREADY-RESOLVED gate out of the
// frame's context — it is never `pending` here, because the frame only provides
// the context once the gate reads `enabled`, so a flag-off or still-resolving
// visitor sees today's page by construction rather than by a branch that could
// get the three-state read wrong (THE-664).
import { useHubFrame } from '../../../theaWeb/hub/HubFrameContext';
import { HubPrimaryButton, HubPrimaryButtonOnPhoto } from '../../../theaWeb/hub/HubFrameParts';
import { useHubOccasionMapping } from '../../../theaWeb/hub/useHubDocs';
import { ComingUpStrip } from '../../../theaWeb/components/ComingUpStrip';
import type { ComingUpEvent } from '../../../theaWeb/schemas/materializedComingUp';
import { MobileTabBarMount } from '../../../theaWeb/calendar/MobileTabBarMount';
import { StickyPrimaryCta, StickyPrimaryCtaMobileSpacer } from './StickyPrimaryCta';
import { Button } from '../../ui/Button';
import type { QuizEntryPoint } from '../../../theaWeb/lib/gaPixel';

export interface LandingPageProps {
  /** Override the default HomepageHero (e.g. for stories that want a
   *  different image or CTA wiring). */
  heroSlot?: React.ReactNode;
  /** Replace the default beats section entirely (audience-targeted homepage
   *  variants). Default: the FeatureStack of HOMEPAGE_STACK_FRAMES. Mirrors
   *  heroSlot. */
  beatsSlot?: React.ReactNode;
  /** Override the EmotionalBanner's two-line heading. Default: the
   *  long-standing "You love them. / Let it show." */
  bannerHeadline?: [string, string];
  /** Override the testimonial set/order (audience variants lead with the
   *  quote closest to their cohort). Default: the shared curated set. */
  testimonials?: Testimonial[];
  /**
   * All homepage sign-in doors route through this single prop. Each call
   * passes its `data-signin-entry` value (homepage-header / homepage-sticky /
   * homepage-boards-row) so the parent can thread it onto the GA4
   * `sign_in_opened` event — same pattern as onCtaClick's entry_point.
   */
  onSignInClick?: (entry: string) => void;
  /**
   * Both the in-page hero CTA and the sticky scroll-CTA route through this
   * single prop; the hero passes `'homepage_hero'` and the sticky passes
   * `'sticky_homepage'` so the parent can thread the surface into the
   * `quiz_start` analytics event (§11.1 entry-point vocabulary).
   */
  onCtaClick?: (entry_point: QuizEntryPoint) => void;
  /**
   * Story/test override: force the signed-in or signed-out branch without
   * touching Firebase auth. When undefined, the live Firebase auth state
   * decides which branch renders.
   */
  authOverride?: 'signed-in' | 'signed-out' | 'loading';
  /**
   * Story/test overrides for the data-driven children of the signed-in
   * layout, mirroring the presentational-override convention (see
   * GiftGuidesPage.stories.tsx / BrowseMyFriendsSection's own props).
   * `authOverride: 'signed-in'` hands BrowseMyFriendsSection a synthetic
   * uid — without these, it would open live Firestore listeners against
   * that fake uid (in Storybook: a crash against the stub db, which is
   * what broke the SignedIn Happo story from #171 until this PR). When
   * `authOverride` is set and these are omitted, safe empty defaults are
   * substituted so no listener ever opens; production callers omit
   * `authOverride` and the live wiring is untouched.
   */
  peopleOverride?: DashboardPerson[];
  loaderOverride?: FriendPreviewLoader;
  /** Story/test injection for the Coming Up strip: fixed events + a fixed
   *  clock for deterministic relative-day labels. When events are injected
   *  the strip's day-of confetti is suppressed (testing.md: no in-flight
   *  animations in stories). */
  comingUpEvents?: ComingUpEvent[];
  comingUpNow?: Date;
  /** Story/test fixtures for the published guide rail; omitted by live pages. */
  guideRailOverride?: React.ComponentProps<typeof HomeGuideRail>;
  /** Override the auth instance for tests. Defaults to the FirebaseProvider's auth. */
  authInstance?: Auth;
}

const Page = styled.div`
  min-height: 100vh;
  background: hsl(var(--background));
  color: hsl(var(--foreground));
  font-family: ${({ theme }) => theme.font.sans};
`;

// `<main>` landmark wrapping the homepage's primary content (WCAG 2.4.1 Bypass
// Blocks / 1.3.1, bug row 123). `display: contents` removes its box so the
// wrapped sections keep flowing exactly as before inside <Page> — landmark role
// preserved (verified in Chrome/Safari 16+). SiteHeader (banner) sits above it;
// Footer + sticky CTA + bottom tab bar sit below, all outside <main>.
const Main = styled.main`
  display: contents;
`;

// Visually-hidden <h1> for the signed-in ("Your people") homepage, which has no
// visible page title — the signed-out branch's <h1> comes from <HomepageHero>.
const HiddenH1 = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/* Zero-height sentinel placed immediately after the hero so an IntersectionObserver
   can drive the sticky CTA. When the sentinel scrolls off the top of the
   viewport, the in-page hero CTA is no longer reachable and the sticky shows. */
const HeroSentinel = styled.div`
  width: 100%;
  height: 1px;
`;

/* Quiet secondary sign-in for an ANON user who already has boards.
   Mirrors the You-page row shipped in #307 — deliberately a hyperlink, not a
   button, so it never competes with the primary "Save your boards" capture.

   Why it exists: this cohort's only sign-in door on the homepage was
   HeaderAccountMenu, which SiteHeader hides below 1024px — so on mobile they
   had NO way in at all. Same class of gap #307 closed on the You tab. See
   `.claude/otp-ux-rethink-spec.md` → "THE INVARIANT — never locked out". */
const HomeSignInRow = styled.div`
  text-align: center;
  margin: 4px 0 20px;
  font-size: 13px;
  color: ${({ theme }) => theme.color.mutedText};
  button {
    background: none;
    border: 0;
    padding: 0;
    margin-left: 4px;
    font: inherit;
    color: ${({ theme }) => theme.color.clay};
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
  }
`;

/* Wordmark shown inside the desktop sticky bar (Kate 2026-06-04). The
   top-of-page SiteHeader wordmark scrolls away; this keeps the brand present
   in the sticky chrome. Mirrors the SiteHeader wordmark style. */
const StickyWordmark = styled.a`
  font-family: ${({ theme }) => theme.font.serif};
  font-style: italic;
  letter-spacing: 0.025em;
  font-size: 24px;
  color: hsl(var(--primary));
  text-decoration: none;
  transition: opacity 200ms ease;
  &:hover {
    opacity: 0.8;
  }
`;

// `useIsSignedIn` lifted to src/theaWeb/hooks/useIsSignedIn.ts so the same
// auth read can drive the StickyPrimaryCta's signInSlot on both the homepage
// and occasion pages (sheet bug #59).

// Inert preview loader substituted when `authOverride` is active but the
// host didn't inject one — same shape BrowseMyFriendsSection uses for its
// own no-uid fallback. Module-level so its identity is stable across
// renders (useFriendPreviews re-subscribes when the loader changes).
const NOOP_PREVIEW_LOADER: FriendPreviewLoader = {
  subscribe: () => () => {},
};

// Hard cap on the post-sign-in settling shimmer. `useAuthInFlux`'s
// `mergeStatus === 'merging'` term is NOT grace-bounded, so a hung/errored
// merge would otherwise keep auth "in flux" forever — wedging a permanent
// zero-board user on a full-page skeleton with no nav. After this cap we fall
// through to the normal (nav-bearing) layout: recoverable, unlike a stuck
// skeleton. Matches AuthGateProvider's own settle safety window.
const AUTH_SETTLE_SHIMMER_MAX_MS = 8000;

export const LandingPage: React.FC<LandingPageProps> = ({
  heroSlot,
  beatsSlot,
  bannerHeadline,
  testimonials,
  onSignInClick,
  onCtaClick,
  authOverride,
  peopleOverride,
  loaderOverride,
  comingUpEvents,
  comingUpNow,
  guideRailOverride,
  authInstance: authInstanceProp,
}) => {
  const ctxAuth = useAuth();
  const authInstance = authInstanceProp ?? ctxAuth;
  const { ready, signedIn } = useIsSignedIn(authOverride, authInstance);
  const gate = useContext(AuthGateContext);
  const hubFrame = useHubFrame();
  // THE-1161's full-width header + edge-to-edge carousels (Kate, 2026-10-28,
  // PR #883: no switch of their own) ride the same resolved hub gate.
  const wideGutterEnabled = hubFrame;

  // Coming Up card taps land exactly where the same event lands on the
  // Calendar (THE-1403), which means the strip needs the same hub-collection
  // knowledge `CalendarPage` reads its routing from — same doc, same
  // skip-when-the-frame-isn't-mounted gate, so a real user pays no extra
  // Firestore subscription and the two surfaces can never disagree.
  const hubOccasionMapping = useHubOccasionMapping(!hubFrame);
  const hubOccasions = hubFrame && hubOccasionMapping.status === 'ready'
    ? hubOccasionMapping.data
    : undefined;
  const hubCollections = useMemo(
    () => (hubOccasions
      ? new Map(hubOccasions.map((occasion) => [occasion.key, occasion.guideSlugs]))
      : undefined),
    [hubOccasions],
  );

  // Resolve the live uid (anon or permanent) so we can read recipient
  // count from Firestore — the layout gate below is "do you have any
  // boards yet?", not "are you signed in?", so both anon-with-boards
  // and permanent users get the same top-of-page search-pill chrome.
  const [authUser, setAuthUser] = useState<User | null>(
    authOverride ? null : authInstance.currentUser,
  );
  useEffect(() => {
    if (authOverride) return;
    const unsub = onAuthStateChanged(authInstance, (next) => setAuthUser(next));
    return () => unsub();
  }, [authInstance, authOverride]);
  const { friends, hydrated: friendsHydrated } = useFriendsList(
    authOverride ? null : authUser?.uid ?? null,
  );

  // Right-grouped nav items for the desktop SiteHeader. Hidden at <1024px
  // via SiteHeader's internal media query; hidden entirely until the
  // user has at least one recipient (so first-time visitors don't see
  // a Calendar link that 404s their experience).
  const navItems = useDesktopNav();

  // Optimistic "has boards" hint, persisted across loads. The friends
  // list re-hydrates from scratch on every mount (Firestore onSnapshot
  // re-subscribe), so on a cross-page hop (e.g. /calendar → /#your-people)
  // `friendsHydrated` is briefly false. Defaulting the layout gate to the
  // hero during that window made board-users watch the marketing hero
  // flash in and disappear. We instead remember whether the user had a
  // board last time and default to the signed-in layout when they did —
  // no hero flicker. Corrected against the live count once hydrated.
  // (Shared helper since spec v5 PR 2 — useDesktopNav reads the same hint.)
  const [hadBoardsHint] = useState(readHasBoardsHint);
  useEffect(() => {
    if (!friendsHydrated) return;
    writeHasBoardsHint(friends.length > 0);
  }, [friendsHydrated, friends.length]);

  // Gate: anyone with ≥1 board gets the "Your People" tile layout at the
  // top; everyone else (first-time visitors, signed-in users who emptied
  // their boards) sees the scrolling marketing hero. Story overrides force
  // the signed-in layout deterministically. Before Firestore hydrates we
  // fall back to the persisted hint so returning board-users skip the
  // hero entirely (see hadBoardsHint above).
  // v4 (spec journey 6): the layout gate is "signed in OR has a board", not
  // board count alone — a signed-in user with nothing saved must still get the
  // People layout + Add-someone tile, never the marketing hero with no way to
  // her account. This mirrors accountState.navUnlocked, which the module's
  // docstring names as the shared predicate showSignedInLayout should call.
  // Gated behind isV4Preview so flag-off stays byte-identical to production.
  const v4SignedIn = isV4Preview() && ready && signedIn;
  const showSignedInLayout =
    authOverride === 'signed-in'
      ? true
      : authOverride
        ? false
        : v4SignedIn
          ? true
          : friendsHydrated
            ? friends.length > 0
            : hadBoardsHint;

  // ONE shared v4 sign-in modal for the homepage (QA row 11): the header door
  // (V4HomeDoor) AND the sticky-footer "Sign in" both open it, per spec journey
  // 1 ("Sign in on the top banner and in the sticky footer"). This replaces the
  // sticky's legacy OTP path under the flag without a second competing modal.
  // Browser kind mirrors useV4AccountState so the Instagram webview gets the
  // email-link variant. Rendered only under the flag; flag-OFF is untouched.
  const [v4SignInOpen, setV4SignInOpen] = useState(false);
  const openV4SignIn = () => setV4SignInOpen(true);
  const v4Browser: BrowserKind = isInAppBrowser() ? 'webview' : 'normal';

  /* Sentinel sits at the bottom edge of the hero region. While it's in the
     viewport, the in-page hero CTA is reachable; once it scrolls off, we show
     the sticky CTA so the primary action is always one tap away. */
  const heroSentinelRef = useRef<HTMLDivElement | null>(null);

  // Hash-scroll handler: the bottom-nav "People" tab and the avatar
  // dropdown "Your People" link both navigate to /#your-people. The
  // homepage hydrates async — the Coming Up strip renders null until its
  // materialized doc loads, recipient tiles stream in, and occasion-tile
  // images decode — so the anchor's final Y isn't known for several
  // hundred ms after mount. A single rAF scroll lands on a stale offset
  // and the page then reflows out from under it (the resting position
  // drifts down into "Browse by occasions"). Re-align to the anchor as
  // the layout settles: re-run on each body resize within a bounded
  // window, and bail the moment the user scrolls manually so we never
  // yank them back.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let cleanup: (() => void) | undefined;
    const scrollToHash = () => {
      cleanup?.();
      const hash = window.location.hash;
      if (!hash || hash.length < 2) return;
      const id = hash.slice(1);
      let aborted = false;
      const align = () => {
        if (aborted) return;
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
      };
      const abort = () => {
        aborted = true;
        cleanup?.();
        cleanup = undefined;
      };
      // User-initiated scroll wins — stop re-aligning.
      window.addEventListener('wheel', abort, { passive: true });
      window.addEventListener('touchmove', abort, { passive: true });
      window.addEventListener('keydown', abort);
      // Re-align whenever the document reflows (Coming Up strip appearing,
      // tiles streaming in, images decoding).
      const ro =
        typeof ResizeObserver !== 'undefined'
          ? new ResizeObserver(() => align())
          : undefined;
      ro?.observe(document.body);
      // Hard cap so we don't observe forever.
      const stop = window.setTimeout(abort, 1500);
      cleanup = () => {
        window.removeEventListener('wheel', abort);
        window.removeEventListener('touchmove', abort);
        window.removeEventListener('keydown', abort);
        ro?.disconnect();
        window.clearTimeout(stop);
      };
      requestAnimationFrame(align);
    };
    scrollToHash();
    window.addEventListener('hashchange', scrollToHash);
    return () => {
      window.removeEventListener('hashchange', scrollToHash);
      cleanup?.();
    };
  }, []);

  // Mobile-OAuth redirect return: the sign-in is finalizing and whichever
  // layout we'd pick right now is about to be wrong (marketing hero for a
  // user who just signed in, or a not-yet-hydrated boards grid). Render the
  // shimmer skeleton until the gate reports the redirect settled — bounded
  // by AuthGateProvider's safety timeout. Read via useContext (not
  // useAuthGate) so hosts without a gate — tests, stories — just skip it.
  // v4 OTP/email sign-in has NO redirect, so `redirectPending` doesn't cover its
  // POST-sign-in settling window: after the code verifies, the permanent user
  // lands but auth stays in flux (`ready` held false), the layout gate falls back
  // to board-count, and a zero-board user briefly sees marketing + no nav until
  // flux settles ("eventual flip", Kate 2026-09-01). Cover that window with the
  // shimmer — only when we'd otherwise show marketing (`!showSignedInLayout`), so
  // a board-haver whose board-derived layout is already resolved keeps their
  // grid-skeleton and a first-time anon (signedIn=false) still gets the hero.
  // `signedIn` is the permanent-user read (not flux-gated); `ready` folds in
  // `!inFlux`.
  //
  // Bounded by AUTH_SETTLE_SHIMMER_MAX_MS so a hung merge (its inFlux term is not
  // grace-bounded) can't wedge it. Scope: covers the window AFTER the permanent
  // user lands — a marketing frame between code-submit and the permanent-user
  // auth event depends on the sign-in modal lifecycle and is out of scope here.
  // Key the shimmer on `authUser` (LandingPage's own live auth read) — the SAME
  // signal BrowseMyFriendsSection uses to decide whether to render the empty
  // state — NOT `useIsSignedIn.signedIn`. The two disagreed during the settling
  // window, so the empty-state card rendered inside the marketing layout (a torn
  // composite) while the `signedIn`-keyed shimmer sat inactive. Keying on the
  // same read makes them agree: whenever a permanent user would show the empty
  // state but the layout hasn't flipped to signed-in yet, the shimmer preempts.
  const settlingShimmer =
    isV4Preview() &&
    !!authUser &&
    !authUser.isAnonymous &&
    !showSignedInLayout;
  const [settleShimmerExpired, setSettleShimmerExpired] = useState(false);
  useEffect(() => {
    if (!settlingShimmer) {
      setSettleShimmerExpired(false);
      return;
    }
    const t = setTimeout(
      () => setSettleShimmerExpired(true),
      AUTH_SETTLE_SHIMMER_MAX_MS,
    );
    return () => clearTimeout(t);
  }, [settlingShimmer]);

  if (gate?.redirectPending || (settlingShimmer && !settleShimmerExpired)) {
    return <HomeSkeleton />;
  }

  /* Coming Up strip auto-hides when the user has no upcoming
     birthdays/anniversaries — so first-time visitors don't see an empty
     section. Rendered ABOVE Your People per Kate's 2026-05-13 layout
     call: an upcoming date is a stronger reason-to-act than a tile grid.

     The #your-people anchor wraps BOTH sections (moved out of
     BrowseMyFriendsSection's heading, Kate 2026-07-10): the "People" tab
     must land with the Coming Up strip in view, not scrolled past it.
     Shared by both layout branches — same anchor, same children.

     When `authOverride` forces the signed-in branch, BrowseMyFriendsSection
     gets a synthetic uid — so it must ALSO get people/loader overrides
     (empty defaults if the host passed none) or it would open live
     Firestore listeners against that fake uid (the SignedIn-story crash). */
  /* Hub: "Also remove the 'Coming up' holiday strip that today appears under
     the hero for first-time visitors […] the strip stays for people who have
     added someone." `showSignedInLayout` IS "has added someone" on this page —
     it is the board-count gate, not an auth read — so the two halves of that
     sentence are the two halves of this condition. The strip self-hides when
     there is nothing upcoming, but holidays are shown to everyone, so without
     this gate a first-time visitor still gets one. */
  const showComingUp = !hubFrame || showSignedInLayout;
  const yourPeopleSection = (
    <div id="your-people" style={{ scrollMarginTop: 96 }}>
      {showComingUp && (
        <ComingUpStrip
          events={comingUpEvents}
          now={comingUpNow}
          confetti={!comingUpEvents}
          wideGutterEnabled={wideGutterEnabled}
          hubCollections={hubCollections}
        />
      )}
      <BrowseMyFriendsSection
        wideGutterEnabled={wideGutterEnabled}
        authOverride={
          authOverride === 'signed-in'
            ? { status: 'signed-in', user: { uid: 'override', initial: 'A' } }
            : undefined
        }
        peopleOverride={authOverride ? peopleOverride ?? [] : peopleOverride}
        loaderOverride={
          authOverride ? loaderOverride ?? NOOP_PREVIEW_LOADER : loaderOverride
        }
        /* The same entry-taking opener the header, boards-row and sticky CTAs
           use. It reaches the Fresh Find Fridays Unlock pill (PR 4.5 /
           THE-770), which is behind that feature's flag and so renders for
           nobody until it is turned on. */
        onSignInClick={onSignInClick}
      />
    </div>
  );

  return (
    <Page>
      {/* Banner: SiteHeader renders in both layouts, hoisted ABOVE <Main> so it
          stays a top-level banner landmark rather than nesting inside main.
          First-time (else) branch overrides `actions` with a plain Sign-in
          rather than the account avatar (Kate 2026-06-04). Its `actions` is
          gated on `ready` (auth-in-flux: on the mobile-OAuth redirect return the
          header paints while the restored ANON session upgrades — an ungated
          "Sign in" reads as "sign-in didn't take", 2026-07-06) AND on `!signedIn`
          (this branch is chosen by BOARD COUNT, not auth, so a signed-in user
          with zero boards lands here and must get the account menu, not a
          perpetual "Sign in" — sheet bug #59 / Kate 2026-07-24). */}
      {showSignedInLayout ? (
        <SiteHeader
          // v4: the has-board homepage door (avatar on desktop, nothing on mobile
          // where the tab bar owns account). undefined = today's default
          // HeaderAccountMenu (flag-off), per SiteHeader's actions default.
          actions={isV4Preview() ? <V4HomeDoor onRequestSignIn={openV4SignIn} /> : undefined}
          onSignInClick={onSignInClick && (() => onSignInClick('homepage-header'))}
          navItems={navItems}
          wideGutterEnabled={wideGutterEnabled}
        />
      ) : (
        <SiteHeader
          wideGutterEnabled={wideGutterEnabled}
          actions={
            isV4Preview() ? (
              // The v4 door owns its ready-gate + state (sign-in / ✓ Sent / avatar)
              // but DEFERS its modal to the shared homepage one, so the header door
              // and the sticky footer open the same modal.
              <V4HomeDoor onRequestSignIn={openV4SignIn} />
            ) : !ready ? null : signedIn ? (
              <HeaderAccountMenu />
            ) : onSignInClick ? (
              <Button
                label="Sign in"
                variant="ghost"
                data-signin-entry="homepage-header"
                onClick={() => onSignInClick('homepage-header')}
              />
            ) : null
          }
          showActionsOnMobile={!signedIn}
          navItems={navItems}
        />
      )}
      <Main>
      {showSignedInLayout ? (
        <>
          {/* The signed-in "Your people" homepage has no visible page title —
              give it a heading so the <main> landmark is named for AT. The
              SiteHeader (with the v4 door) is hoisted above <Main> as a banner. */}
          <HiddenH1>Your people</HiddenH1>
          {yourPeopleSection}
          {/* v4 (spec row 12 / journey 5): the persistent sign-in ask moves to
              the You tab, so this inline OTP row is REMOVED under the flag — the
              door for this cohort is the bottom tab bar → You card (mobile) and
              the header avatar (desktop). Kept flag-OFF verbatim: it is the
              flag-OFF anon-with-board mobile homepage's only inline door, guarded
              by signInAlwaysReachable.test.tsx, so removing it un-gated would
              regress production and re-open the #291/#301 lockout. */}
          {/* Hub (PRD, People tab): "Has people: same as today, except remove
              'Made boards on another device? Sign in' […] the You tab is their
              way to sign in" — the SAME substitution v4 made, reached through
              the hub gate, so the row survives for a visitor on neither flag.
              Under the hub frame the door is the bottom bar's You tab, which
              the frame renders unconditionally on this page. */}
          {!isV4Preview() && !hubFrame && ready && !signedIn && onSignInClick ? (
            <HomeSignInRow data-testid="home-signin-row">
              Made boards on another device?
              <button
                type="button"
                data-signin-entry="homepage-boards-row"
                onClick={() => onSignInClick('homepage-boards-row')}
              >
                Sign in
              </button>
            </HomeSignInRow>
          ) : null}
        </>
      ) : (
        <>
          {/* First-time header (SiteHeader with the v4 door + the ready/!signedIn
              gating) is hoisted above <Main> as a banner landmark — see the
              showSignedInLayout ternary above. */}
          {heroSlot ?? (
            <HomepageHero
              headline={['Your gifting brain,', 'all in one place.']}
              accentLine={1}
              tightHeadline
              compactBottom
              subhead={HOMEPAGE_HERO_SUBHEAD}
              /* A signed-in user with zero boards reaches this branch too
                 (the gate is board count, not auth). "Get started" is a dead
                 phrase for someone who just did — name the next actual step.

                 Hub (PRD, People tab): "the button changes from 'Get started'
                 to 'Add your first person'". That wording already names the
                 next actual step, so it REPLACES both labels rather than only
                 the signed-out one — the special case existed to fix exactly
                 what the new copy fixes. */
              ctaLabel={hubFrame ? 'Add your first person' : signedIn ? 'Start your first board' : undefined}
              /* Near-black, not the clay gradient: PRD Visual style makes
                 near-black the system primary and reserves rust for auth
                 (canvas discrepancy 3). See HubPrimaryButton. */
              ctaSlot={hubFrame ? (label, onClick) => (
                <HubPrimaryButton type="button" onClick={onClick}>{label}</HubPrimaryButton>
              ) : undefined}
              onCtaClick={() => onCtaClick?.('homepage_hero')}
              // THE-1081: 56px/700 headline + 20px regular-gray subhead
              // (was 64px/800 + 24px/500-at-78%-opacity) — the Airbnb-comp
              // type cleanup. Flag-off and every other HomepageHero caller
              // (/moms, /teacher-appreciation) are untouched.
              compactHeadline={hubFrame}
            />
          )}
          <HeroSentinel ref={heroSentinelRef} aria-hidden="true" />
          {/* Anon users with persisted recipients (boards-architecture
              proposal §3 — every quiz submission creates an anon-uid
              recipient) see their tile grid here, between the marketing
              hero and the value-props card. The section guards on
              `people.length === 0` internally so first-time visitors
              never see it; only post-quiz anons. */}
          {yourPeopleSection}
        </>
      )}
      {/* Marketing chrome — the trending-guides rail + testimonials — lives on
          the homepage only for the no-boards (first-time / emptied-boards)
          view. Once the user has ≥1 board these move out of the way: the
          guides get a dedicated home in the Gift Guides nav tab
          (/gift-guides), and a returning board-user doesn't need the
          social-proof pitch. Gated on the same has-boards flag that drives
          the layout swap. */}
      {!showSignedInLayout && (
        <>
          {/* Category-education beats (device-framed) teach the ongoing,
              multi-person, gets-better model — the fix for the "one-and-done
              search" misread. The emotional banner then bookends the page
              before social proof.

              The trending-guides rail sits ABOVE testimonials (Kate,
              2026-09-09): it replaces the "Browse gift guides" occasion tiles,
              which linked to /occasion/* pages rather than the curated guides
              and left the real library invisible to first-time visitors. Its
              "See all" is also this page's only link to /gift-guides — the nav
              link and tab bar both gate on having a board — so it must come
              before the social-proof block a scanning visitor may never reach.
              The /occasion/* routes themselves are untouched. */}
          {/* Kate, 2026-10-26: back to the stacked explainer on the People
              tab too, same as today's live page — this supersedes the PRD's
              "move to one sideways swipe row" line. The frame on vs off no
              longer forks this block. */}
          {beatsSlot ?? <FeatureStack frames={HOMEPAGE_STACK_FRAMES} wideGutterEnabled={wideGutterEnabled} />}
          <EmotionalBanner
            headline={bannerHeadline ?? HOMEPAGE_BANNER_HEADLINE}
            ctaLabel={signedIn ? 'Start your first board' : undefined}
            onCtaClick={() => onCtaClick?.('homepage_banner')}
            // THE-1081: the banner heading drops to section-title size
            // (28/26px, 700) instead of hero size (48/32px, 800), and "Get
            // started" becomes the white-on-photo form of the one primary
            // button (HubPrimaryButtonOnPhoto) instead of the clay pill.
            compact={hubFrame}
            ctaSlot={hubFrame ? (label, onClick) => (
              <HubPrimaryButtonOnPhoto type="button" onClick={onClick}>{label}</HubPrimaryButtonOnPhoto>
            ) : undefined}
          />
          <HomeGuideRail wideGutterEnabled={wideGutterEnabled} {...guideRailOverride} />
          <TestimonialsCarousel
            items={testimonials ?? HOMEPAGE_TESTIMONIALS}
            // THE-1081: left-aligned 26/28px section-title heading, flat
            // 18px quote, regular-weight gray attribution — italics stay
            // the one deliberate type exception.
            compact={hubFrame}
            wideGutterEnabled={wideGutterEnabled}
          />
        </>
      )}
      </Main>
      {/* The StickyPrimaryCta's "Find a gift" + Sign-in slot is for
          signed-out users only. Board-users (signed-in layout) get no
          sticky chrome — their entry point is the AddPersonTile grid.

          Hub (PRD, People tab): "Remove the sticky 'Get started' bar at the
          bottom: the page already has 'Add your first person' and the pinned
          'Log in'." Both of those are now true on this page under the frame —
          the hero CTA is the near-black "Add your first person" and the frame's
          pinned mobile bar carries "Log in" — so the sticky's two jobs are
          already done above the fold and it goes, spacer and all. The frame's
          bottom tab bar is the thing that holds this edge of the screen now. */}
      {!showSignedInLayout && !hubFrame && (
        <>
          <StickyPrimaryCtaMobileSpacer />
          <StickyPrimaryCta
            triggerRef={heroSentinelRef}
            hidePrimaryCta
            desktopLeftSlot={
              <StickyWordmark href="/" aria-label="Thea — home">
                thea
              </StickyWordmark>
            }
            onCtaClick={() => onCtaClick?.('sticky_homepage')}
            signInSlot={
              // Hide the secondary "Sign in" CTA once the user is
              // authenticated (sheet bug #59). We wait for `ready` so the
              // bootstrap moment doesn't flash a Sign-in button and then
              // yank it once auth lands. Anon Firebase users still see the
              // CTA — they aren't "signed in" for product purposes.
              // Under v4 the sticky opens the shared v4 modal (Google/Apple/
              // password, or the Instagram email-link) instead of the legacy
              // OTP modal (QA row 11). Flag-OFF keeps the onSignInClick path,
              // byte-identical to production.
              ready && !signedIn ? (
                isV4Preview() ? (
                  <Button
                    label="Sign in"
                    variant="ghost"
                    data-signin-entry="homepage-sticky"
                    onClick={openV4SignIn}
                  />
                ) : onSignInClick ? (
                  <Button
                    label="Sign in"
                    variant="ghost"
                    data-signin-entry="homepage-sticky"
                    onClick={() => onSignInClick('homepage-sticky')}
                  />
                ) : null
              ) : null
            }
          />
        </>
      )}
      {/* The ONE shared v4 sign-in modal (QA row 11) — opened by the header door
          and the sticky footer alike. Null when closed; rendered only under the
          flag, so flag-OFF is byte-identical. */}
      {isV4Preview() ? (
        <V4SignInModal
          open={v4SignInOpen}
          onClose={() => setV4SignInOpen(false)}
          browser={v4Browser}
          intent="signin"
          boardCount={friends.length}
          onSignedIn={() => setV4SignInOpen(false)}
        />
      ) : null}
      {/* Bottom tab bar — the has-board mobile account door.
          FLAG-OFF: `forceVisible` is undefined, so MobileTabBarMount keeps its
          own `useFriendsList` gate (appears once the user has ≥1 recipient) —
          byte-identical to production.
          v4: the bar's visibility is TIED to `showSignedInLayout`, the same
          predicate the sticky "Sign in" rides (the sticky renders iff
          `!showSignedInLayout`). That makes the bar (You-tab door) and the
          sticky EXACT COMPLEMENTS, so a signed-out mobile visitor sees EXACTLY
          ONE of them after auth settles. This closes the zero-door race: the
          bar previously computed its own `useFriendsList` visibility, but the
          layout optimistically shows "Your people" (suppressing the sticky) via
          `hadBoardsHint` BEFORE `friendsHydrated` — so every returning
          board-user cold load had a window with the sticky gone and the bar not
          yet up (and an unbounded version if that separate listener errored).
          Reading one predicate removes the divergence; during bootstrap the
          hint decides which single door shows (the accepted neutral window when
          the hint is false is the same anti-flash placeholder every door uses). */}
      <MobileTabBarMount
        active="people"
        forceVisible={isV4Preview() ? showSignedInLayout : undefined}
      />
    </Page>
  );
};
